import { useEffect, useMemo, useState } from "react";
import RichText from "./RichText";

function normalizeParts(parts = []) {
  return parts.map((p) => ({
    t: String(p?.t || ""),
    w: p?.w || "medium",
  }));
}

function buildSourceParts(text, parts) {
  if (Array.isArray(parts) && parts.length > 0) {
    return normalizeParts(parts);
  }

  return [{ t: String(text || ""), w: "medium" }];
}

function cutParts(parts = [], visibleChars = 0) {
  let left = visibleChars;
  const result = [];

  for (const part of parts) {
    const txt = String(part?.t || "");
    if (left <= 0) break;

    if (txt.length <= left) {
      result.push(part);
      left -= txt.length;
    } else {
      result.push({
        ...part,
        t: txt.slice(0, left),
      });
      left = 0;
    }
  }

  return result;
}

export default function TypingRichText({
  text = "",
  parts = [],
  className = "",
  speed = 14,
  enabled = true,
  onDone,
}) {
  const sourceParts = useMemo(() => buildSourceParts(text, parts), [text, parts]);

  const sourceKey = useMemo(
    () => sourceParts.map((p) => `${p.w}:${p.t}`).join("|"),
    [sourceParts]
  );

  const fullLength = useMemo(
    () => sourceParts.reduce((sum, p) => sum + String(p.t || "").length, 0),
    [sourceParts]
  );

  const [visibleChars, setVisibleChars] = useState(enabled ? 0 : fullLength);

  useEffect(() => {
    setVisibleChars(enabled ? 0 : fullLength);
  }, [sourceKey, enabled, fullLength]);

  useEffect(() => {
    if (!enabled) {
      onDone?.();
      return;
    }

    if (visibleChars >= fullLength) {
      onDone?.();
      return;
    }

    const timer = window.setTimeout(() => {
      setVisibleChars((prev) => Math.min(prev + 1, fullLength));
    }, speed);

    return () => window.clearTimeout(timer);
  }, [enabled, visibleChars, fullLength, speed, onDone]);

  const visibleParts = enabled
    ? cutParts(sourceParts, visibleChars)
    : sourceParts;

  return <RichText className={className} text="" parts={visibleParts} />;
}